import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  ClientReportStatus,
  ClientReportsGQL,
  ClientReportsQuery
} from '../../../../generated/graphql';
import { RelayIdService } from '../../../shared/relay-id.service';
import { ReportHtmlDialogComponent } from './report-html-dialog/report-html-dialog.component';

type ReportRow = ClientReportsQuery['clientReports']['edges'][number]['node'];

const PAGE_SIZE = 25;

/** Company card → "Ataskaitos": what was sent to this client, across all its orders. */
@Component({
  selector: 'app-company-reports',
  templateUrl: './company-reports.component.html',
  styleUrls: ['./company-reports.component.scss']
})
export class CompanyReportsComponent implements OnInit, OnDestroy {
  readonly columns = ['sentAt', 'order', 'recipients', 'period', 'status', 'figures', 'open'];
  readonly statuses = ClientReportStatus;
  readonly pageSize = PAGE_SIZE;

  rows: ReportRow[] = [];
  totalCount = 0;
  loading = false;

  private companyId: string;
  private cursors: string[] = [null];
  private pageIndex = 0;
  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private reportsGQL: ClientReportsGQL,
    private relayIdService: RelayIdService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.route.parent.paramMap.subscribe(params => {
      // the route id is already the company's GraphQL global id, like the other company tabs use it
      this.companyId = params.get('id');
      this.reload();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.load();
  }

  open(row: ReportRow): void {
    this.dialog.open(ReportHtmlDialogComponent, { data: { reportId: row.id }, width: '720px', autoFocus: false });
  }

  orderNumber(row: ReportRow): string {
    return this.relayIdService.getNumeric(row.order.id);
  }

  private reload(): void {
    this.cursors = [null];
    this.pageIndex = 0;
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.reportsGQL.fetch({
      company: this.companyId,
      first: PAGE_SIZE,
      after: this.cursors[this.pageIndex]
    }, { fetchPolicy: 'network-only' }).subscribe({
      next: ({ data }) => {
        const connection = data.clientReports;
        this.rows = connection.edges.map(edge => edge?.node).filter(Boolean);
        this.totalCount = connection.totalCount || 0;
        this.cursors[this.pageIndex + 1] = connection.pageInfo.endCursor;
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }
}
